import { memo, useCallback, useMemo, useState } from 'react'
import { CrewConfirmDialog } from 'components/elements/crewConfirmDialog/crewConfirmDialog'
import OutlineVideocam from '~icons/ic/outline-videocam'
import BaselineDelete from '~icons/ic/baseline-delete'
import BaselineSaveAlt from '~icons/ic/baseline-save-alt'
import { useCrewMeetingArchiveItem } from './useCrewMeetingArchiveItem'
import { CrewLink } from 'components/elements/crewLink/crewLink'
import { useModal } from 'components/layouts/modal/useModal'
import { useToast } from 'hooks/useToast'
import { useTranslation } from '@crew/modules/i18n'

import { useUserSetting } from '@crew/states'
import { Region, SettingKeyType } from '@crew/enums/app'
import { EventRecording } from '@crew/models/domain'
import { RecordingType, StreamState } from '@crew/enums/domain'
import { isStreamableFile } from '@crew/utils/file'

export type CrewMeetingArchiveItemProps = {
  recordingInfo: EventRecording
  showDeleteButton: boolean
  showDownloadButton: boolean
  onLinkClick: () => void // リンククリック時のイベント、動画を再生する
}

/**
 * ファイル名表示コンポーネント
 */
const FileName = memo((props: { fileName: string }) => (
  <div className="flex items-center gap-x-2">
    <OutlineVideocam width={24} height={24} />
    <span>{props.fileName}</span>
  </div>
))

export const CrewMeetingArchiveItem = memo(
  (props: CrewMeetingArchiveItemProps) => {
    const {
      getEventRecordingUrl,
      deleteEventRecording,
      isLoadingDeleteEventRecording,
    } = useCrewMeetingArchiveItem()

    const { t } = useTranslation()

    // ユーザー設定からデフォルトのユーザープロファイル地域を取得
    const defaultUserProfileRegion = useUserSetting(
      SettingKeyType.UserProfileRegion,
      Region.Japan.value
    )

    // 開始日フォーマット
    const formatedStartDate = useMemo(() => {
      return {
        datetime: t('format.datetime', {
          value: props.recordingInfo.startDatetime,
          timeZone: defaultUserProfileRegion,
        }),
        date: t('format.date', {
          value: props.recordingInfo.startDatetime,
          timeZone: defaultUserProfileRegion,
        }),
      }
    }, [defaultUserProfileRegion, props.recordingInfo.startDatetime, t])

    // 終了日フォーマット
    const formatedEndDate = useMemo(() => {
      return {
        datetime: t('format.datetime', {
          value: props.recordingInfo.endDatetime,
          timeZone: defaultUserProfileRegion,
        }),
        date: t('format.date', {
          value: props.recordingInfo.endDatetime,
          timeZone: defaultUserProfileRegion,
        }),
        shorttime: t('format.shorttime', {
          value: props.recordingInfo.endDatetime,
          timeZone: defaultUserProfileRegion,
        }),
      }
    }, [defaultUserProfileRegion, props.recordingInfo.endDatetime, t])

    // 再生可能かどうか
    // 1. ストリーム状態がReadyの場合、かつ
    // 2-a. 録画typeがInternal、もしくは
    // 2-b. ファイルの拡張子が対応拡張子の場合のみ再生可能
    const isStreamable = useMemo(() => {
      if (props.recordingInfo.streamState !== StreamState.Ready) {
        // 再生不可
        return false
      }

      if (props.recordingInfo.recordingType === RecordingType.Internal) {
        // 内部録画ファイルは常に再生可能
        return true
      }

      // 外部録画ファイルでも対応拡張子の場合は再生可能
      if (props.recordingInfo.fileName != null) {
        if (isStreamableFile(props.recordingInfo.fileName)) {
          return true
        }
      }

      // それ以外は再生不可
      return false
    }, [
      props.recordingInfo.fileName,
      props.recordingInfo.recordingType,
      props.recordingInfo.streamState,
    ])

    // ストリーム状態によってprefixを生成する
    const fileNamePrefix = useMemo(() => {
      switch (props.recordingInfo.streamState) {
        case StreamState.Unprocessed:
        case StreamState.Processing:
          return `(${t('label.processState.processing')}) `
        case StreamState.Failed:
          return `(${t('label.processState.failed')}) `
        case StreamState.Ready:
          return ''
        default:
          return `(${t('label.unknown')}) `
      }
    }, [props.recordingInfo.streamState, t])

    // ファイル名
    const fileName = useMemo(() => {
      // アップロードしたファイルの場合はファイル名をそのまま返す。
      // 万一ファイル名が記録されていない場合はデフォルト値を返す。
      if (props.recordingInfo.recordingType === RecordingType.External) {
        return props.recordingInfo.fileName ?? 'recording.mp4'
      }

      // Crewで録画したファイルの場合は開始日時と終了日時からファイル名を生成する
      // two same dates
      if (formatedStartDate.date === formatedEndDate.date) {
        return `${formatedStartDate.datetime} - ${formatedEndDate.shorttime}`
      }
      // two different dates
      return `${formatedStartDate.datetime} - ${formatedEndDate.datetime}`
    }, [
      formatedEndDate.date,
      formatedEndDate.datetime,
      formatedEndDate.shorttime,
      formatedStartDate.date,
      formatedStartDate.datetime,
      props.recordingInfo.fileName,
      props.recordingInfo.recordingType,
    ])

    const [isConfirmDialogOpen, openConfirmDialog, closeConfirmDialog] =
      useModal()
    const { success, error } = useToast()

    // 確認ダイアログメッセージ
    const [confirmMessage, setConfirmMessage] = useState('')

    // ダウンロードボタンクリック時
    const handleDownloadButtonClick = useCallback(async () => {
      // 録画ファイルの署名付きURLを取得
      const url = await getEventRecordingUrl(props.recordingInfo.id)

      // 署名付きURLから録画ファイルをダウンロード
      const link = document.createElement('a')
      link.href = url
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }, [fileName, getEventRecordingUrl, props.recordingInfo.id])

    // Action when delete button is clicked
    const handleDeleteButtonClick = useCallback(() => {
      // 確認ダイアログの表示（処理は確認ダイアログのOKボタン押下時に行う）
      setConfirmMessage(t('message.general.confirmMessage.delete'))
      openConfirmDialog()
    }, [openConfirmDialog, t])

    // 削除確認ダイアログのOKボタン押下
    const handleDeletePermitButtonClick = useCallback(async () => {
      try {
        await deleteEventRecording(props.recordingInfo.id)
        success(t('message.meetingRecording.meetingRecordingDeleted'))
      } catch (err) {
        error(t('message.general.failedToDelete'))
        return
      }
      closeConfirmDialog()
    }, [
      closeConfirmDialog,
      deleteEventRecording,
      props.recordingInfo,
      success,
      t,
      error,
    ])

    return (
      <>
        <div className="flex gap-2 items-center">
          {isStreamable ? (
            <CrewLink
              // 動画再生
              onClick={props.onLinkClick}
              title={fileName}
            >
              <FileName fileName={`${fileNamePrefix}${fileName}`} />
            </CrewLink>
          ) : (
            <FileName fileName={`${fileNamePrefix}${fileName}`} />
          )}

          {/* ダウンロードアイコン（ボタン） */}
          {props.showDownloadButton && (
            <div
              className="cursor-pointer hover:opacity-70"
              onClick={handleDownloadButtonClick}
            >
              <BaselineSaveAlt width={24} height={24} />
            </div>
          )}
          {/* 削除アイコン（ボタン） */}
          {props.showDeleteButton && (
            <div
              className="cursor-pointer hover:opacity-70"
              onClick={handleDeleteButtonClick}
            >
              <BaselineDelete width={24} height={24} />
            </div>
          )}
        </div>

        {/* 削除確認ダイアログ */}
        <CrewConfirmDialog
          isOpen={isConfirmDialogOpen}
          message={confirmMessage}
          onPermitButtonClick={handleDeletePermitButtonClick}
          onCancelButtonClick={closeConfirmDialog}
          permitButtonDisabled={isLoadingDeleteEventRecording}
        />
      </>
    )
  }
)
